import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core'
import { debounce } from 'lodash-es'
import { NanoPopPosition } from 'nanopop'
import { css } from 'src/lib/emotion'
import { gsap } from 'src/lib/gsap'
import { SpacingType } from 'src/tailwind'

import { classes, colorClasses } from '../../utils/css'

// WARNING(aleksclark): prefer ui-infotip if it meets your needs

// Mouse events can come from all sorts of child elements which can cause
// unwanted behavior, this class is applied to the tooltip parent so that it's
// the only element that triggers the mouse events (other than the tooltip
// itself), if you need events for any reason you can apply the
// `pointer-events-auto` class on the required elements
const noChildEvents = css`
  position: relative;
  > * {
    pointer-events: none;
  }
`

const defaultBackgroundColor = '#5D5D5D'
@Component({
  tag: 'ui-tooltip',
})
export class Tooltip {
  @Element() el: HTMLUiTooltipElement

  @Event() closed: EventEmitter

  @Event() popperEnter: EventEmitter
  @Event() popperLeave: EventEmitter

  @Prop({ reflect: true }) name: string
  @Prop() anchor: NanoPopPosition = 'top'
  @Prop() bgColor = defaultBackgroundColor
  @Prop() borderColor = this.bgColor
  @Prop() on: 'hover' | 'click' | 'none' | 'group:hover' = 'hover'
  @Prop() timeout = 1500
  @Prop() persist = false
  @Prop() autoFocus = false
  @Prop() margin = 15
  @Prop({ mutable: true }) parentEl: HTMLElement & { activeTooltip?: HTMLUiTooltipElement }
  @Prop() keepFocus = false
  @Prop() disablePointer = false
  @Prop() disableAnimation = false
  @Prop() allowClick = false
  @Prop() disabled = false
  @Prop() inline = false
  @Prop() contentWidth: SpacingType

  @Prop() isDreamtenTooltip = false
  @Prop() debug = false

  @State() active = false

  resizeObserver: ResizeObserver
  hideTimeout: number

  hide = () => {
    if (this.debug) {
      return
    }
    // keep open if element inside tooltip has focus
    if (!this.keepFocus || !this.el.contains(document.activeElement)) {
      this.active = false
    }
  }

  onParentClick = () => {
    if (this.disabled) {
      return
    }
    if (this.on === 'click') {
      this.active = true
      if (!this.persist) {
        this.startHideTimeout()
      }
    } else {
      // eslint-disable-next-line
      window.setTimeout(this.hide)
    }
  }

  onParentEnter = () => {
    this.onParentLeave.cancel()
    this.over = true
    if (this.disabled || (this.parentEl.activeTooltip && this.parentEl.activeTooltip !== this.el)) {
      return
    }
    if (['hover', 'group:hover'].includes(this.on)) {
      this.parentEl.classList.add('z-menu')
      window.clearTimeout(this.hideTimeout)
      this.active = true
    }
  }

  onParentLeave = debounce(() => {
    this.over = false
    if (this.disabled || !this.ready) {
      return
    }
    if (this.on.includes('hover')) {
      this.hide()
      this.parentEl.classList.remove('z-menu')
    }
  }, 100)

  hitareaEl: HTMLDivElement
  ready: boolean
  over: boolean

  startHideTimeout() {
    window.clearTimeout(this.hideTimeout)
    this.hideTimeout = window.setTimeout(this.hide, this.timeout)
  }

  setAutoFocus() {
    const firstInput = this.popperEl.querySelector<HTMLInputElement>('input, textarea')
    if (this.autoFocus && firstInput) {
      firstInput.focus()
      firstInput.selectionStart = firstInput.selectionEnd = firstInput.value.length
    }
  }

  popperEl: HTMLUiPopperElement
  contentEl: HTMLDivElement

  @Listen('click', { target: 'window' })
  onOutsideClick(event) {
    if (this.disabled) {
      return
    }
    if (!this.el.contains(event.target) && !this.parentEl.contains(event.target)) {
      this.close()
    }
  }

  @Method()
  async close() {
    this.active = false
    this.popperLeave.emit()
  }

  @Watch('active')
  onActive() {
    if (this.disabled) {
      return
    }
    this.popperEl.visible = this.active
    if (!this.active) {
      if (this.parentEl.activeTooltip === this.el) {
        this.parentEl.activeTooltip = null
      }
      this.closed.emit()
    }
    if (this.active) {
      this.parentEl.activeTooltip = this.el
      if (this.name) {
        Array.from<HTMLUiTooltipElement>(document.querySelectorAll(`ui-tooltip[name="${this.name}"]`))
          .filter((el) => el !== this.el)
          .map((e) => e.close())
      }
      this.ready = false
      window.clearTimeout(this.hideTimeout)
      gsap.killTweensOf(this.contentEl)
      const transformOrigin = {
        'top': 'center bottom',
        'top-middle': 'center bottom',
        'top-start': 'left bottom',
        'top-end': 'right bottom',
        'right': 'left center',
        'right-middle': 'left center',
        'right-start': 'left top',
        'right-end': 'left bottom',
        'bottom': 'center top',
        'bottom-middle': 'center top',
        'bottom-start': 'left top',
        'bottom-end': 'right top',
        'left': 'right center',
        'left-middle': 'right center',
        'left-start': 'right top',
        'left-end': 'right bottom',
      }[this.popperEl.currentPosition]
      if (this.persist) {
        this.positionHitArea()
      }
      if (!this.disableAnimation) {
        gsap.fromTo(
          this.contentEl,
          { scale: 0.8, transformOrigin },
          {
            scale: 1,
            duration: 0.2,
            ease: 'back.out',
            onComplete: () => {
              this.ready = true
              if (!this.over) {
                this.onParentLeave()
              }
              this.setAutoFocus()
            },
          }
        )
      } else {
        this.ready = true
        this.setAutoFocus()
      }
    }
  }

  /**
   * Positions the hit area element between the tooltip and trigger based on
   * which direction/position the tooltip is using
   */
  positionHitArea() {
    const parentRect = this.parentEl.getBoundingClientRect()
    const popperRect = this.contentEl.getBoundingClientRect()

    const [side] = this.popperEl.currentPosition.split('-')

    const hitRect: DOMRect = {
      top: new DOMRect(
        Math.min(popperRect.left, parentRect.left),
        popperRect.bottom,
        Math.max(popperRect.width, parentRect.width),
        parentRect.top - popperRect.bottom
      ),
      right: new DOMRect(
        parentRect.right,
        Math.min(parentRect.top, popperRect.top),
        popperRect.left - parentRect.right,
        Math.max(popperRect.height, parentRect.height)
      ),
      bottom: new DOMRect(
        Math.min(popperRect.left, parentRect.left),
        parentRect.bottom,
        Math.max(popperRect.width, parentRect.width),
        popperRect.top - parentRect.bottom
      ),
      left: new DOMRect(
        popperRect.right,
        Math.min(parentRect.top, popperRect.top),
        parentRect.left - popperRect.right,
        Math.max(popperRect.height, parentRect.height)
      ),
    }[side]
    // Expand the rectangle by 5px on all side so it overlaps a bit
    hitRect.x -= 5
    hitRect.y -= 5
    hitRect.width += 10
    hitRect.height += 10
    const { x, y, width, height } = hitRect
    gsap.set(this.hitareaEl, { x, y, width, height })
  }

  componentDidLoad() {
    if (this.debug) {
      this.active = true
    }
  }

  connectedCallback() {
    this.parentEl = this.parentEl || this.el.parentElement || document.createElement('div')
    this.parentEl.addEventListener('click', this.onParentClick)
    this.parentEl.classList.add(noChildEvents)
    if (this.on === 'group:hover') {
      this.el.closest('.group').addEventListener('mouseenter', this.onParentEnter)
      this.el.closest('.group').addEventListener('mouseleave', this.onParentLeave)
    } else {
      this.parentEl.addEventListener('mouseenter', this.onParentEnter)
      this.parentEl.addEventListener('mouseleave', this.onParentLeave)
    }
  }

  disconnectedCallback() {
    this.parentEl.removeEventListener('click', this.onParentClick)
    this.parentEl.classList.remove(noChildEvents)
    if (this.on === 'group:hover') {
      this.el.closest('.group').removeEventListener('mouseover', this.onParentEnter)
      this.el.closest('.group').removeEventListener('mouseleave', this.onParentLeave)
    } else {
      this.parentEl.removeEventListener('mouseover', this.onParentEnter)
      this.parentEl.removeEventListener('mouseleave', this.onParentLeave)
    }
  }

  render() {
    return (
      <Host class='absolute pointer-events-none'>
        <ui-popper
          margin={this.margin}
          inline={this.inline}
          ref={(el) => (this.popperEl = el)}
          position={this.anchor}
          reference={this.parentEl}
          popperMouseEnter={() => {
            !this.inline && this.onParentEnter()
            this.popperEnter.emit()
          }}
          popperMouseLeave={() => {
            !this.inline && this.onParentLeave()
            this.popperLeave.emit()
          }}
          popperClick={(e) => {
            const el = e.target as HTMLElement
            if (!this.allowClick) {
              e.stopImmediatePropagation()
            }
            if (el.closest('[close-tooltip]')) {
              !this.inline && this.onParentLeave()
              this.close()
            }
          }}
        >
          {this.persist && (
            <div
              // This element is used to cover the gap between parent and tooltip
              // when `persist` is set to true, this will make sure the menu doesn't
              // close prematurely due to the mouse "leaving" the target
              ref={(el) => (this.hitareaEl = el)}
              class={`fixed top-0 left-0 w-full h-10 bg-info-error bg-opacity-0`}
            />
          )}
          <div
            ref={(el) => (this.contentEl = el)}
            class={`
              font-semibold normal-case px-7px py-4px z-pin border text-xs rounded text-white opacity-100
              ${classes(!!this.contentWidth, `w-${this.contentWidth}`)}
              ${colorClasses({
                bg: this.bgColor,
                border: this.borderColor,
              })}
            `}
            style={
              this.bgColor !== 'transparent' && {
                boxShadow: '0 0 0 1px rgba(0,0,0,0.1), 0 2px 15px -4px rgba(0,0,0,0.1)',
              }
            }
          >
            {this.isDreamtenTooltip ? <slot name='dreamten' /> : <slot />}
          </div>
          {!this.disablePointer && (
            <ui-pointer fillColor={this.bgColor} strokeColor={this.borderColor} strokeWidth={1} width={16} height={8} />
          )}
        </ui-popper>
      </Host>
    )
  }
}
